import React, { useState, useMemo } from "react";
import "../UIcss/Alerts.css";
import {
  FaTimes,
  FaCode,
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaBell,
  FaShieldAlt,
  FaStar,
  FaBolt,
} from "react-icons/fa";
import { LuCopy, LuInfo } from "react-icons/lu";
import Pagination from "./Pagination.jsx";
import CodeViewer from "./CodeViewer.jsx";

const getAlertData = (index) => {
  const types = ["success", "error", "warning", "info"];
  const styles = [
    "solid",
    "outline",
    "left-accent",
    "glass",
    "modern",
    "minimal",
    "neon",
    "gradient",
  ];

  const iconConfigs = [
    {
      icon: <FaCheckCircle />,
      title: "Action Success",
      msg: "Your changes have been saved successfully.",
    },
    {
      icon: <FaBell />,
      title: "Notification",
      msg: "You have a new message in your inbox.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Security Alert",
      msg: "Your account was accessed from a new device.",
    },
    {
      icon: <FaStar />,
      title: "Premium Feature",
      msg: "Unlock new possibilities with our latest update.",
    },
    {
      icon: <FaBolt />,
      title: "Fast Delivery",
      msg: "Your order is being processed at lightning speed.",
    },
    {
      icon: <FaExclamationCircle />,
      title: "System Error",
      msg: "Something went wrong while processing the data.",
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Critical Warning",
      msg: "Please review your system logs immediately.",
    },
    {
      icon: <FaInfoCircle />,
      title: "Information",
      msg: "You can customize these alerts in your settings.",
    },
  ];

  const type = types[Math.floor(index / 5) % types.length];
  const style = styles[Math.floor(index / 20) % styles.length];
  const configIndex = index % iconConfigs.length;
  const config = iconConfigs[configIndex];

  const styleLabels = {
    solid: "Solid",
    outline: "Outline",
    "left-accent": "Left Accent",
    glass: "Glassmorphic",
    modern: "Modern Soft",
    minimal: "Minimalist",
    neon: "Neon Glow",
    gradient: "Dynamic Gradient",
  };

  return {
    id: index + 1,
    className: `alert-${index + 1}`,
    type,
    style,
    label: `${styleLabels[style]} ${config.title} ${(index % 5) + 1}`,
    icon: config.icon,
    title: config.title,
    message: config.msg,
  };
};

// --- HELPER TO GET CODE ---
const getGeneratedCode = (config) => {
  const iconSvgs = {
    "Action Success": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 512 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm108.25 138.29l-134.4 160a16 16 0 01-12 5.71h-.27a16 16 0 01-11.89-5.3l-57.6-64a16 16 0 1123.78-21.4l45.29 50.32 122.59-145.91a16 16 0 0124.5 20.58z\"></path></svg>`,
    Notification: `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 448 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64zm215.39-149.71c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71z\"></path></svg>`,
    "Security Alert": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 512 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M466.5 83.7l-192-80a48.15 48.15 0 00-36.9 0l-192 80C27.7 91.1 16 108.6 16 128c0 198.5 114.5 335.7 221.5 380.3 11.3 4.7 29.5 4.7 40.9 0C385.5 463.7 500 326.5 500 128c0-19.4-11.7-36.9-29.5-44.3zM256 464C155 422.5 54.4 300 48.5 133.9L256 47.4l207.5 66.5C457.6 300 357 422.5 256 464z\"></path></svg>`,
    "Premium Feature": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 576 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z\"></path></svg>`,
    "Fast Delivery": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 320 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M296 160H180.6l42.6-129.8C227.7 15 215.7 0 200 0H56C44 0 33.8 8.9 32.2 20.8l-32 240C-1.7 275.2 9.5 288 24 288h118.7L85.2 482.3c-5.1 15.3 4.8 30.5 20.3 30.5 10.4 0 19.9-6.3 24-16l186.7-448c8.2-19.8-6.7-38.8-20.2-38.8z\"></path></svg>`,
    "System Error": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 512 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm0 319.91a20 20 0 1120-20 20 20 0 01-20 20zm21.72-201.15l-5.74 122a16 16 0 01-32 0l-5.74-121.95a21.73 21.73 0 0121.5-22.69h.48a21.74 21.74 0 0121.5 22.64z\"></path></svg>`,
    "Critical Warning": `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 576 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0L569.517 440.013zM288 354c-18.778 0-34 15.222-34 34s15.222 34 34 34 34-15.222 34-34-15.222-34-34-34zm6.236-18.754l6.4-128c.301-6.021-4.515-11.246-10.586-11.246h-4.1c-6.071 0-10.887 5.225-10.586 11.246l6.4 128c.271 5.429 4.754 9.754 10.186 9.754h6.1c5.432 0 9.915-4.325 10.186-9.754z\"></path></svg>`,
    Information: `<svg stroke=\"currentColor\" fill=\"currentColor\" stroke-width=\"0\" viewBox=\"0 0 512 512\" height=\"1em\" width=\"1em\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm0 319.91a20 20 0 1120-20 20 20 0 01-20 20zm21.72-201.15l-5.74 122a16 16 0 01-32 0l-5.74-121.95a21.73 21.73 0 0121.5-22.69h.48a21.74 21.74 0 0121.5 22.64z\"></path></svg>`,
  };

  const currentIcon = iconSvgs[config.title] || "<!-- Icon SVG -->";

  const htmlCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset=\"UTF-8\">
    <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">
    <title>${config.label} - CodePulse UI</title>
    <!-- Include Google Fonts -->
    <link href=\"https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&display=swap\" rel=\"stylesheet\">
    <style>
        /* Paste the CSS from the CSS tab here */
    </style>
</head>
<body>
    <!-- Complete Component HTML for ${config.label} -->
    <div class=\"alert alert-${config.type} style-${config.style} ${config.className}\">
      <div class=\"alert-icon\">
        ${currentIcon}
      </div>
      <div class=\"alert-content\">
        <div class=\"alert-title\">${config.title}</div>
        <div class=\"alert-message\">${config.message}</div>
      </div>
      <div class=\"alert-close\">
        <button class=\"close-btn\" aria-label=\"Close alert\">&times;</button>
      </div>
    </div>
</body>
</html>`;

  const cssCode = `/* Base Alert Styles */
.alert {
  display: flex;
  align-items: flex-start;
  padding: 12px 15px;
  border-radius: 10px;
  gap: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  font-family: sans-serif;
}

.alert-icon { font-size: 22px; flex-shrink: 0; margin-top: 2px; }
.alert-content { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.alert-title { font-weight: 700; font-family: 'Orbitron', sans-serif; text-transform: uppercase; }
.alert-message { font-size: 0.9rem; opacity: 0.85; line-height: 1.5; }
.alert-close { cursor: pointer; opacity: 0.6; font-size: 14px; margin-left: 10px; }

/* Type Styles */
.alert-${config.type} { 
  background: ${
    config.type === "success"
      ? "linear-gradient(135deg, #00fa9a, #00c853)"
      : config.type === "error"
        ? "linear-gradient(135deg, #ff4d4d, #d32f2f)"
        : config.type === "warning"
          ? "linear-gradient(135deg, #ffd700, #ffa000)"
          : "linear-gradient(135deg, #00bfff, #0288d1)"
  }; 
  color: ${config.type === "error" ? "#fff" : "#000"};
}

/* Style Variant: ${config.style} */
${
  config.style === "outline"
    ? `.style-outline { background: transparent !important; border: 1.5px solid currentColor !important; }`
    : config.style === "left-accent"
      ? `.style-left-accent { background: #1e1e1e !important; border-left: 5px solid currentColor !important; color: #fff !important; }`
      : config.style === "glass"
        ? `.style-glass { background: rgba(255, 255, 255, 0.03) !important; backdrop-filter: blur(15px); border: 1px solid rgba(255, 255, 255, 0.1); color: #fff !important; }`
        : config.style === "modern"
          ? `.style-modern { background: #ffffff05 !important; border-radius: 16px; border: 1px solid #ffffff10; color: #fff !important; }`
          : config.style === "neon"
            ? `.style-neon { background: #0a0a0a !important; border: 1px solid currentColor !important; box-shadow: 0 0 20px currentColor; color: inherit; }`
            : config.style === "gradient"
              ? `.style-gradient { background: linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)) !important; border: 1px solid rgba(255,255,255,0.1); color: #fff !important; }`
              : "/* Solid style uses the base type background */"
}`;

  return { html: htmlCode, css: cssCode };
};

const AlertRenderer = ({ config, onViewCode }) => {
  return (
    <div className="alert-item-container">
      <div className="alert-meta-header">
        <span className="alert-label">{config.label}</span>
        <button
          className="alert-copy-btn"
          onClick={(e) => {
            e.stopPropagation();
            onViewCode(config);
          }}
        >
          <LuCopy />
        </button>
      </div>

      <div className="alert-preview">
        <div className={`alert ${config.className} style-${config.style}`}>
          <div className="alert-icon">{config.icon}</div>
          <div className="alert-content">
            <div className="alert-title">{config.title}</div>
            <div className="alert-message">{config.message}</div>
          </div>
          <div className="alert-close">
            <FaTimes />
          </div>
        </div>
      </div>
    </div>
  );
};

const Alerts = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  const totalItems = 200;

  const allAlerts = useMemo(() => {
    return Array.from({ length: totalItems }, (_, i) => getAlertData(i));
  }, []);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeConfig, setActiveConfig] = useState(null);

  const handleViewCode = (config) => {
    setActiveConfig(config);
    setViewerOpen(true);
  };

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return allAlerts.slice(indexOfFirstItem, indexOfLastItem);
  }, [currentPage, allAlerts]);

  const generatedCode = useMemo(() => {
    if (!activeConfig) return { html: "", css: "" };
    return getGeneratedCode(activeConfig);
  }, [activeConfig]);

  return (
    <div className="alerts-page">
      <div className="library-header">
        <div className="header-content">
          <h1>
            <FaBell /> Alerts <span>Library</span>
          </h1>
          {/* <p>Modern, contextual feedback components for every state.</p> */}
        </div>
      </div>

      <div className="alerts-list">
        {currentItems.map((config) => (
          <AlertRenderer
            key={config.id}
            config={config}
            onViewCode={handleViewCode}
          />
        ))}
      </div>

      <div className="alerts-pagination-wrapper">
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          onPageChange={setCurrentPage}
        />
      </div>

      {viewerOpen && activeConfig && (
        <CodeViewer
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          componentName={activeConfig.label}
          jsxCode={generatedCode.html}
          cssCode={generatedCode.css}
        />
      )}
    </div>
  );
};

export default Alerts;
